import { getSiteConfig, getPortalBase, requestPortalLoginLink } from '@/api/client';
import { getAdapter } from '@/authAdapter';
import { ensureEzAuth } from '@/ensureEzAuth';
import { logError } from '@/sdkConsole';

// resolvePortalUrl returns the URL the subscriber portal tab should open to: a
// one-click portal login URL for a verified visitor (ezAuth token, or the BYO
// publisher's SM-minted identity token), so they land signed into the portal
// without an email round-trip, or the bare portal URL otherwise. A soft (BYO
// Tier 1 / guest) identity is deliberately never bridged — the backend mints a
// link only for a verified credential — so a plaintext-email BYO adapter, a
// guest, an unrecognized visitor, and any failure all fall back to the bare
// portal, which runs the portal's own sign-in.
export async function resolvePortalUrl(): Promise<string> {
  const config = await getSiteConfig();
  if (typeof config?.domainId !== 'number') {
    return getPortalBase();
  }
  if (config.authMode === 'ezauth') {
    const token = await ensureEzAuth(config.domainId);
    if (!token) {
      return getPortalBase();
    }
    const url = await requestPortalLoginLink({ ezAuthToken: token });
    return url ?? getPortalBase();
  }
  if (config.authMode === 'byo') {
    const token = await byoIdentityToken();
    if (!token) {
      return getPortalBase();
    }
    const url = await requestPortalLoginLink({ identityToken: token });
    return url ?? getPortalBase();
  }
  return getPortalBase();
}

// byoIdentityToken pulls a fresh SM-minted identity token from the publisher's
// auth adapter (mint-on-demand per the adapter contract). Null when no adapter
// is wired, the adapter has no getIdentityToken, the visitor is logged out, or
// the adapter throws — all degrade to the bare portal.
async function byoIdentityToken(): Promise<string | null> {
  const getIdentityToken = getAdapter()?.getIdentityToken;
  if (typeof getIdentityToken !== 'function') {
    return null;
  }
  try {
    const token = await getIdentityToken();
    return typeof token === 'string' && token.trim() ? token.trim() : null;
  } catch (err) {
    logError('portal handoff getIdentityToken failed', err);
    return null;
  }
}

// openSubscriberPortal opens the subscriber portal in a new tab — seamlessly for
// an ezAuth-verified member (the tab lands already signed in) and as a plain
// portal visit otherwise. It returns false when the browser blocked the popup.
//
// The tab is opened synchronously so the browser treats it as user-initiated (an
// async open after resolvePortalUrl's awaits is eaten by popup blockers): open a
// blank tab we control, sever its opener link so the portal can't reach back into
// the publisher page, then navigate it once the destination URL resolves.
export function openSubscriberPortal(): boolean {
  const tab = window.open('', '_blank');
  if (!tab) {
    return false;
  }
  tab.opener = null;
  void resolvePortalUrl()
    .catch((err) => {
      // resolvePortalUrl already degrades to the bare portal internally, so a
      // rejection here is unexpected; fall back rather than leaving a blank tab.
      logError('portal handoff failed', err);
      return getPortalBase();
    })
    .then((url) => {
      if (!tab.closed) {
        tab.location.replace(url);
      }
    });
  return true;
}
